import {
  PARALLEL_AGENTS,
  SEQUENTIAL,
  SCAN_ONLY_LANE_KEYS,
  type AgentConfig,
  type AgentLaneState,
} from '../components/FirstActionFlow/agentConfig';
import type { OnboardingRunStep } from '../api/onboardingRuns';

type LanePhase = AgentLaneState['phase'];

interface BuildProgressFlags {
  hasImages?: boolean;
  hasReviews?: boolean;
  isComplete?: boolean;
}

type StepLike = Pick<OnboardingRunStep, 'lane' | 'status' | 'weight'>;

export function lanePhasesFromSteps(steps: StepLike[]): Record<string, LanePhase> {
  const byLane = new Map<string, { total: number; done: number; started: number; failed: boolean }>();

  for (const step of steps) {
    if (step.status === 'skipped') continue;
    const lane = byLane.get(step.lane) ?? { total: 0, done: 0, started: 0, failed: false };
    lane.total += step.weight;
    if (step.status === 'done') lane.done += step.weight;
    if (step.status !== 'pending') lane.started += 1;
    if (step.status === 'failed') lane.failed = true;
    byLane.set(step.lane, lane);
  }

  const phases: Record<string, LanePhase> = {};
  for (const [lane, w] of byLane) {
    if (w.started === 0) continue;
    if (w.failed) phases[lane] = 'failed';
    else phases[lane] = w.total > 0 && w.done === w.total ? 'done' : 'running';
  }
  return phases;
}

export function visibleBuildLanes(flags: BuildProgressFlags = {}): AgentConfig[] {
  const enabled: Record<string, boolean | undefined> = {
    hasImages: flags.hasImages,
    hasReviews: flags.hasReviews,
  };
  const parallel = PARALLEL_AGENTS.filter(a => !a.visibilityFlag || enabled[a.visibilityFlag] !== false);
  return [...parallel, ...SEQUENTIAL];
}

export function buildLanePhases(
  lanePhases: Record<string, LanePhase>,
  flags: BuildProgressFlags = {},
): Record<string, LanePhase> {
  const merged: Record<string, LanePhase> = {};
  for (const agent of visibleBuildLanes(flags)) merged[agent.key] = 'queued';
  for (const key of SCAN_ONLY_LANE_KEYS) if (key in merged) merged[key] = 'done';
  for (const [lane, phase] of Object.entries(lanePhases)) if (lane in merged) merged[lane] = phase;
  if (flags.isComplete) for (const agent of SEQUENTIAL) merged[agent.key] = 'done';

  const parallelDone = visibleBuildLanes(flags)
    .filter(a => !SEQUENTIAL.some(s => s.key === a.key))
    .every(a => a.key === 'siteHealth' || merged[a.key] === 'done');

  if (!parallelDone) {
    for (const agent of SEQUENTIAL) {
      if (merged[agent.key] !== 'done' && merged[agent.key] !== 'failed') merged[agent.key] = 'queued';
    }
  }
  return merged;
}

interface BuildProgress {
  doneCount: number;
  totalCount: number;
  pct: number;
}

export function countBuildProgress(
  lanePhases: Record<string, LanePhase | undefined>,
  flags: BuildProgressFlags = {},
): BuildProgress {
  const lanes = visibleBuildLanes(flags);
  const doneCount = lanes.filter(a => lanePhases[a.key] === 'done').length;
  const totalCount = lanes.length;
  return { doneCount, totalCount, pct: totalCount > 0 ? Math.round((doneCount / totalCount) * 100) : 0 };
}

export function buildProgressFromSteps(steps: StepLike[], flags: BuildProgressFlags = {}): BuildProgress {
  return countBuildProgress(buildLanePhases(lanePhasesFromSteps(steps), flags), flags);
}
